import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import { Card, Button, Input, Textarea, Select, Badge, Modal } from '../components/ui';
import { 
  Plus, 
  Image, 
  Search, 
  Filter,
  X,
  Calendar,
  MapPin,
  Palette,
  Upload,
  Loader2,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Collection = () => {
  const { t } = useTranslation();
  const [tattoos, setTattoos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedTattoo, setSelectedTattoo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStyle, setFilterStyle] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    body_location: '',
    style: '',
    date_done: '',
    notes: '',
  });

  const bodyLocations = [
    'Kol', 'Ön Kol', 'El', 'Omuz', 'Göğüs', 'Sırt', 
    'Karın', 'Bacak', 'Baldır', 'Ayak', 'Boyun', 'Kafa'
  ];

  const styles = [
    'Blackwork', 'Geometric', 'Traditional', 'Neo Traditional', 
    'Realistic', 'Watercolor', 'Minimalist', 'Japanese', 'Tribal',
    'Fine Line', 'Dotwork', 'Script', 'Abstract'
  ];

  useEffect(() => {
    fetchTattoos();
  }, []);

  const fetchTattoos = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tattoos`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTattoos(data);
      }
    } catch (error) {
      console.error('Failed to fetch tattoos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const method = selectedTattoo ? 'PUT' : 'POST';
      const url = selectedTattoo 
        ? `${API_URL}/api/tattoos/${selectedTattoo.tattoo_id}`
        : `${API_URL}/api/tattoos`;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        fetchTattoos();
        setShowModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Failed to save tattoo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tattooId) => {
    if (!window.confirm(t('collection.confirmDelete', 'Are you sure you want to delete this tattoo?'))) return;

    try {
      const response = await fetch(`${API_URL}/api/tattoos/${tattooId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        fetchTattoos();
      }
    } catch (error) {
      console.error('Failed to delete tattoo:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      body_location: '',
      style: '',
      date_done: '',
      notes: '',
    });
    setSelectedTattoo(null);
  };

  const openEditModal = (tattoo) => {
    setSelectedTattoo(tattoo);
    setFormData({
      name: tattoo.name || '',
      description: tattoo.description || '',
      body_location: tattoo.body_location || '',
      style: tattoo.style || '',
      date_done: tattoo.date_done ? tattoo.date_done.split('T')[0] : '',
      notes: tattoo.notes || '',
    });
    setShowModal(true);
  };

  const filteredTattoos = tattoos.filter(tattoo => {
    const matchesSearch = tattoo.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         tattoo.body_location?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStyle === 'all' || tattoo.style === filterStyle;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl mb-1" data-testid="collection-title">
              {t('collection.title', 'Tattoo')} <span className="text-primary">{t('collection.titleHighlight', 'Collection')}</span>
            </h1>
            <p className="text-muted-foreground">
              {tattoos.length} {t('collection.recorded', 'tattoos recorded')}
            </p>
          </div>
          <Button onClick={() => { resetForm(); setShowModal(true); }} data-testid="add-tattoo-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('collection.addNew', 'Add New')}
          </Button>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('collection.search', 'Search tattoos...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12"
              data-testid="search-input"
            />
          </div>
          <Select
            value={filterStyle}
            onChange={(e) => setFilterStyle(e.target.value)}
            className="sm:w-48"
            data-testid="style-filter"
          >
            <option value="all">{t('collection.allStyles', 'All Styles')}</option>
            {styles.map(style => (
              <option key={style} value={style}>{style}</option>
            ))}
          </Select>
        </div>

        {/* Tattoo Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
          </div>
        ) : filteredTattoos.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {filteredTattoos.map((tattoo, index) => (
              <motion.div
                key={tattoo.tattoo_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="overflow-hidden group" data-testid={`tattoo-card-${index}`}>
                  {/* Image */}
                  <div className="relative h-48 bg-muted">
                    {tattoo.photos?.[0]?.url ? (
                      <img 
                        src={tattoo.photos[0].url} 
                        alt={tattoo.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-12 h-12 text-muted-foreground" />
                      </div>
                    )}
                    
                    {/* Overlay Actions */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="text-white hover:text-primary"
                        onClick={() => openEditModal(tattoo)}
                      >
                        <Eye className="w-5 h-5" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="text-white hover:text-primary"
                        onClick={() => openEditModal(tattoo)}
                      >
                        <Edit className="w-5 h-5" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="text-white hover:text-destructive"
                        onClick={() => handleDelete(tattoo.tattoo_id)}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>

                    {/* Status Badge */}
                    <Badge 
                      variant={tattoo.healing_status === 'healed' ? 'success' : 'warning'}
                      className="absolute top-3 right-3"
                    >
                      {tattoo.healing_status === 'healed' ? t('dashboard.healed') : t('dashboard.healing')}
                    </Badge>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h3 className="font-heading text-lg mb-2 truncate">{tattoo.name}</h3>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{tattoo.body_location}</span>
                      </div>
                      {tattoo.style && (
                        <div className="flex items-center gap-2">
                          <Palette className="w-4 h-4" />
                          <span>{tattoo.style}</span>
                        </div>
                      )}
                      {tattoo.date_done && (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(tattoo.date_done).toLocaleDateString('tr-TR')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <Card className="p-12 text-center">
            <Image className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading text-xl mb-2">{t('collection.empty', 'No Tattoos Yet')}</h3>
            <p className="text-muted-foreground mb-6">
              {t('collection.emptyDescription', 'Start by adding your first tattoo.')}
            </p>
            <Button onClick={() => setShowModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('collection.addFirst', 'Add Your First Tattoo')}
            </Button>
          </Card>
        )}

        {/* Add/Edit Modal */}
        <AnimatePresence>
          {showModal && (
            <Modal
              isOpen={showModal}
              onClose={() => { setShowModal(false); resetForm(); }}
              title={selectedTattoo ? t('collection.edit') : t('collection.addNew')}
            >
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">{t('collection.formName', 'Tattoo Name')} *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder={t('collection.namePlaceholder', 'E.g.: Phoenix Arm Tattoo')}
                    required
                    data-testid="tattoo-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">{t('collection.formDesc', 'Description')}</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder={t('collection.descPlaceholder', 'Notes about your tattoo...')}
                    rows={3}
                    data-testid="tattoo-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">{t('collection.formBodyPart', 'Body Location')} *</label>
                    <Select
                      value={formData.body_location}
                      onChange={(e) => setFormData({...formData, body_location: e.target.value})}
                      required
                      data-testid="tattoo-location-select"
                    >
                      <option value="">{t('common.select', 'Select')}</option>
                      {bodyLocations.map(loc => (
                        <option key={loc} value={loc}>{loc}</option>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">{t('collection.formStyle', 'Style')}</label>
                    <Select
                      value={formData.style}
                      onChange={(e) => setFormData({...formData, style: e.target.value})}
                      data-testid="tattoo-style-select"
                    >
                      <option value="">{t('common.select', 'Select')}</option>
                      {styles.map(style => (
                        <option key={style} value={style}>{style}</option>
                      ))}
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Yaptırılma Tarihi *</label>
                  <Input
                    type="date"
                    value={formData.date_done}
                    onChange={(e) => setFormData({...formData, date_done: e.target.value})}
                    required
                    data-testid="tattoo-date-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">{t('collection.formNotes', 'Notes')}</label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder={t('collection.notesPlaceholder', 'Artist info, price, etc...')}
                    rows={2}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => { setShowModal(false); resetForm(); }}
                  >
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button type="submit" className="flex-1" disabled={loading} data-testid="save-tattoo-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.save')}
                  </Button>
                </div>
              </form>
            </Modal>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default Collection;
